/**
 * permissions.js
 * 
 * Centralized role-based permission helpers for the project member system.
 * 
 * Role hierarchy: owner > member > community > null (logged out)
 * 
 * Members are stored in the portal document (portals/{portal_id}).members[]
 * Each member has: { uid, role, status, invite_code, email?, added_at }
 */

/**
 * Compute the current user's project role based on portal data.
 * 
 * @param {Object|null} currentUser - Firebase auth user object
 * @param {Object|null} portal - Portal document data (from state.app.portal)
 * @returns {"owner"|"member"|"community"|null} The user's role
 */
export function computeUserRole(currentUser, portal) {
  if (!currentUser) return null;
  if (currentUser.uid === portal?.creator_id) return "owner";
  const members = portal?.members || [];
  const match = members.find(m => m.uid === currentUser.uid && m.status === "accepted");
  if (match) return "member";
  return "community";
}

// ── Permission check helpers ──

/** Can the user add/delete/reorder shots? */
export const canEditShots = (role) => role === "owner" || role === "member";

/** Can the user add/delete/reorder scenes (parts)? */
export const canEditParts = (role) => role === "owner" || role === "member";

/** Can the user edit shot titles? */
export const canEditShotTitle = (role) => role === "owner" || role === "member";

/** Can the user edit shot descriptions? */
export const canEditShotDescription = (role) => role === "owner" || role === "member";

/** Can the user access the Story Audio sidebar link? */
export const canAccessStoryAudio = (role) => role === "owner" || role === "member";

/** Can the user access the View as Strip sidebar link? */
export const canViewAsStrip = (role) => role === "owner" || role === "member";

/** Can the user start workflows? */
export const canStartWorkflow = (role) => role === "owner" || role === "member";

/** Can the user manage project members (add/remove)? Owner only. */
export const canManageMembers = (role) => role === "owner";

/** Can the user manage project settings? Owner only. */
export const canManageSettings = (role) => role === "owner";

/** Is the user at least a member (owner or member)? General-purpose check. */
export const isAtLeastMember = (role) => role === "owner" || role === "member";
